import { ReactNode } from 'react';

import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';
import { useOnboardingDialog, OnboardingDialogId } from '@/new/hooks/useOnboardingDialog';
import { cn } from '@/new/lib/utils';

interface OnboardingDialogProps {
  dialogId: OnboardingDialogId;
  icon: string;
  title: string;
  children: ReactNode;
  buttonText?: string;
  onTakeTour?: () => void;
}

/**
 * A reusable onboarding dialog that shows on first visit and persists dismissal in localStorage.
 */
export function OnboardingDialog({
  dialogId,
  icon,
  title,
  children,
  buttonText = 'Got it!',
  onTakeTour,
}: OnboardingDialogProps) {
  const { isOpen, closeDialog } = useOnboardingDialog(dialogId);

  const handleTakeTour = () => {
    closeDialog();
    onTakeTour?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            <Icon className={cn('h-5 w-5 text-teal-500', icon)} />
            {title}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription asChild>
          <div className="space-y-3 text-sm text-gray-600">{children}</div>
        </DialogDescription>
        <DialogFooter className={cn('justify-center', onTakeTour && 'justify-between')}>
          {onTakeTour && (
            <Button variant="default" className="gap-2 text-gray-600" onClick={handleTakeTour}>
              <Icon className="lucide--map h-4 w-4" />
              Take a Tour
            </Button>
          )}
          <Button variant="primary" className="px-6" onClick={closeDialog}>
            {buttonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
